import { profilesService, leadsService } from './supabase';
import { DEMO_USER_ID, setDemoMode, isDemoMode } from './dataScope';
import { isBurgerNavLayout } from '../utils/mobileContext';

export type DemoWorkspaceStatus = 'active' | 'cleared' | 'deferred';

export interface DemoWorkspaceState {
  status: DemoWorkspaceStatus;
  activatedAt?: string;
  clearedAt?: string;
  deferredAt?: string;
}

const DEMO_PREF_KEY = 'demoWorkspace';

function readDemoState(preferences: unknown): DemoWorkspaceState | null {
  if (!preferences || typeof preferences !== 'object' || Array.isArray(preferences)) return null;
  const raw = (preferences as Record<string, unknown>)[DEMO_PREF_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const status = (raw as DemoWorkspaceState).status;
  if (status !== 'active' && status !== 'cleared' && status !== 'deferred') return null;
  return raw as DemoWorkspaceState;
}

async function persist(state: DemoWorkspaceState): Promise<DemoWorkspaceState> {
  await profilesService.mergePreferences({ [DEMO_PREF_KEY]: state });
  return state;
}

export async function getDemoWorkspaceState(): Promise<DemoWorkspaceState | null> {
  const profile = await profilesService.getProfile();
  if (!profile) return null;
  return readDemoState(profile.preferences);
}

/** Apply in-memory data scope from preferences (call early in app shell). */
export async function syncDemoModeFromPreferences(): Promise<DemoWorkspaceState | null> {
  if (!DEMO_USER_ID) {
    setDemoMode(false);
    return null;
  }
  const state = await getDemoWorkspaceState();
  // Never scope to demo account while the burger shell is shown.
  setDemoMode(state?.status === 'active' && !isBurgerNavLayout());
  return state;
}

/**
 * Decide demo mode for first visit / resume / layout switch:
 * - cleared → personal account
 * - active + burger → pause to deferred (personal account + warning)
 * - active + left rail → demo account + tour
 * - deferred + burger → personal account + warning
 * - deferred + left rail → activate (unless user already has own leads → cleared)
 * - empty + burger → deferred
 * - empty + left rail → active
 */
export async function ensureDemoWorkspace(): Promise<DemoWorkspaceState | null> {
  if (!DEMO_USER_ID) {
    setDemoMode(false);
    return null;
  }

  const onBurgerLayout = isBurgerNavLayout();
  const existing = await getDemoWorkspaceState();

  if (existing?.status === 'cleared') {
    setDemoMode(false);
    return existing;
  }

  // Mid-tour on a narrow shell: leave demo data immediately (avoid being stuck on demo account).
  if (existing?.status === 'active' && onBurgerLayout) {
    const deferred: DemoWorkspaceState = {
      status: 'deferred',
      deferredAt: new Date().toISOString(),
    };
    await persist(deferred);
    setDemoMode(false);
    return deferred;
  }

  if (existing?.status === 'active') {
    setDemoMode(true);
    return existing;
  }

  if (existing?.status === 'deferred') {
    if (onBurgerLayout) {
      setDemoMode(false);
      return existing;
    }

    // Left rail again: resume demo only if the personal workspace is still empty.
    setDemoMode(false);
    const ownLeads = await leadsService.getAll();
    if (ownLeads.length > 0) {
      const cleared: DemoWorkspaceState = {
        status: 'cleared',
        clearedAt: new Date().toISOString(),
      };
      await persist(cleared);
      setDemoMode(false);
      return cleared;
    }

    const active: DemoWorkspaceState = {
      status: 'active',
      activatedAt: new Date().toISOString(),
    };
    await persist(active);
    setDemoMode(true);
    return active;
  }

  // No pref yet — count real user's leads
  setDemoMode(false);
  const ownLeads = await leadsService.getAll();
  if (ownLeads.length > 0) {
    const cleared: DemoWorkspaceState = {
      status: 'cleared',
      clearedAt: new Date().toISOString(),
    };
    await persist(cleared);
    setDemoMode(false);
    return cleared;
  }

  if (onBurgerLayout) {
    const deferred: DemoWorkspaceState = {
      status: 'deferred',
      deferredAt: new Date().toISOString(),
    };
    await persist(deferred);
    setDemoMode(false);
    return deferred;
  }

  const active: DemoWorkspaceState = {
    status: 'active',
    activatedAt: new Date().toISOString(),
  };
  await persist(active);
  setDemoMode(true);
  return active;
}

/** Leave demo mode → empty personal workspace. */
export async function clearDemoWorkspace(): Promise<void> {
  const cleared: DemoWorkspaceState = {
    status: 'cleared',
    clearedAt: new Date().toISOString(),
  };
  await persist(cleared);
  setDemoMode(false);
}

export function demoModeEnabled(): boolean {
  return isDemoMode();
}
